import React from 'react';
import _ from 'lodash';
import cx from 'classnames';
import { ReportTablePracticeCell } from './ReportTablePracticeCell';
import { ReportTable } from '../../../shared/components/ReportTable';
import { AppraisalReportsService } from '../../../shared/services/appraisalReportsService';

/**
 * @typedef {object} DetailedReportTableProps
 * @property {Object} [appraisalOrgUnit]
 * @property {boolean} [isPercentageVisible]
 * @property {Array<*>} [practiceAreas]
 * @property {number} [selectedPracticeId]
 * @property {(selected: { practiceArea: Object, practice: Object }) => void} handlePracticeSelected
 * @property {boolean} loading
 */

/**
 * @param {DetailedReportTableProps} props
 */
export const DetailedReportTable = props => {
  const {
    appraisalOrgUnit,
    isPercentageVisible,
    practiceAreas,
    selectedPracticeId,
    handlePracticeSelected,
    loading
  } = props;

  const maxPracticeGroupLevels = appraisalOrgUnit
    ? AppraisalReportsService.getMaxLevelWithinGroups([appraisalOrgUnit], false, null)
    : null;

  const getHeaderCells = () => {
    if (!appraisalOrgUnit) {
      return [];
    }

    const headerCells = maxPracticeGroupLevels.flatMap(group =>
      _.range(1, group.maxNumberWithinLevel + 1).map(practiceLevel =>
        renderPracticeAreaHeaderCell(group.groupLevel, practiceLevel, group.maxNumberWithinLevel)
      )
    );

    isPercentageVisible &&
      headerCells.push(renderAggragatePercetangeHeaderCell(appraisalOrgUnit.AggregatePercentage));

    return headerCells;
  };

  const getDataRows = () => {
    if (!appraisalOrgUnit) {
      return [];
    }

    const reportPracticeAreas = AppraisalReportsService.getAppraisalOrgUnitDetailedReportData(
      appraisalOrgUnit,
      maxPracticeGroupLevels,
      isPercentageVisible,
      practiceAreas
    );

    const rows = reportPracticeAreas.map(practiceAreaRow => {
      const practiceAreaCells = practiceAreaRow.practices.map(practice => {
        const maxLevelWithinGroup = maxPracticeGroupLevels.find(
          group => group.groupLevel === practice.groupLevel
        ).maxNumberWithinLevel;

        const key = `${practiceAreaRow.practiceArea.id}.${practice.groupLevel}.${practice.numberWithinLevel}`;
        return (
          <ReportTablePracticeCell
            key={key}
            practiceArea={practiceAreaRow.practiceArea}
            practice={practice}
            isLastPracticeInGroup={practice.numberWithinLevel === maxLevelWithinGroup}
            isSelected={practice.id === selectedPracticeId}
            handleSelected={handlePracticeSelected}
          />
        );
      });

      return renderPracticeAreaRow(
        practiceAreaRow.practiceArea,
        practiceAreaCells,
        isPercentageVisible,
        practiceAreaRow.aggregatePercentage
      );
    });

    return rows;
  };

  const renderPracticeAreaHeaderCell = (groupLevel, practiceLevel, maxNumberWithinLevel) => {
    return (
      <th
        key={groupLevel + '.' + practiceLevel}
        className={cx(
          {
            'report-table__border-right': practiceLevel === maxNumberWithinLevel
          },
          'report-table__cell--centered-below'
        )}>
        {groupLevel}.{practiceLevel}
      </th>
    );
  };

  const renderAggragatePercetangeHeaderCell = aggregatePercentage => {
    return (
      <th
        key='percentage-column'
        className='report-table__percent'
        data-testid='table-percentage-header'>
        {aggregatePercentage}%
      </th>
    );
  };

  const renderPracticeAreaRow = (practiceArea, cells, isPercentageVisible, aggregatePercentage) => {
    return (
      <tr key={practiceArea.id} className='report-table__row--sticky-column'>
        <td className='report-table__border-right report-table__pa-name'>
          {practiceArea.displayName}
        </td>
        {cells}
        {isPercentageVisible && (
          <td className='report-table__border-right' data-testid='table-percentage-row'>
            {aggregatePercentage.practiceAreaPercentage}%
          </td>
        )}
      </tr>
    );
  };

  return (
    <ReportTable
      white
      loading={loading}
      headers={
        <tr className='report-table__row--sticky-first-row report-table__row--sticky-column'>
          <th className='report-table__border-right'>Practice Area</th>
          {getHeaderCells()}
        </tr>
      }
      rows={getDataRows()}
    />
  );
};
